<script setup lang="ts">
import type { CustomerDto, CustomerResponse } from '@/types';
import { onMounted, ref, watch } from 'vue';
import axios from 'axios';
import { onBeforeRouteLeave } from 'vue-router';
import local from '@/stores/local';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import BackComponent from '@/components/BackComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import { useCustomerStore } from '@/stores/customer';
import { storeToRefs } from 'pinia';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import { useIframeStore } from '@/stores/iframe';

const customerStore = useCustomerStore()
const { agreement } = storeToRefs(useAgreementStore())
const { config } = storeToRefs(useConfigStore())
const iframeStore = useIframeStore()

const valid = ref(true)
const customerForm = ref<Partial<CustomerDto>>({})
const locked = ref(false)
const rules = ref({
  required: (value: unknown) => !!value || "Påkrævet",
  name: (value: string) =>
    (!!value &&
      value.split(" ").length >= 2 &&
      value
        .split(" ")
        .every((x) => /^[a-zA-Z0-9\u00c0-\u017e.:/-]+$/.test(x))) ||
    "Skal bestå af minimum 2 navne.",
  email: (value: string) =>
    /^[^\W_]+(?!.*([\W_]{2}|[\W_]@|@[\W_]))[\w-.]*@[\w-.]+\.[a-z]{2,5}$/i.test(value) ||
    "Skal indeholde et @ og slutte på .dk, .com e.lign.",
  address: (value: string) => {
    if (!value) return false

    const parts = value.split(" ");
    const partsMatch = parts.every((x) => /^[a-zA-Z\d\u00c0-\u017e.]+$/.test(x));
    const oneWord = parts.some((x) => /^[a-zA-Z\u00c0-\u017e.]{2,}$/.test(x));
    const oneDigit = parts.some((x) => /[\d]{1,}/.test(x));

    return ((partsMatch && oneWord && oneDigit) || "Skal indeholde minimum ét ord og ét tal.");
  },
  zipCode: (value: string) =>
    /^[\d]{4}$/.test(value) ||
    "Må kun indeholde tal og skal være 4 cifre.",
  mobile: (value: string) =>
    /^[\d]{8}$/.test(value) ||
    "Må kun indeholde tal og skal være 8 cifre.",
  accepted: (value: boolean) => value || "Du skal acceptere betingelserne.",
})

async function getCustomer(focused: boolean) {
  if (!focused && customerForm.value.email && rules.value.email(customerForm.value.email) === true) {
    try {
      const { data } = await axios.get(`proxy/customers?email=${customerForm.value.email}`)

      if (data.length) {
        locked.value = true
        customerForm.value = data[0]
        customerForm.value.zipCode = data[0].zipcode // Needed because response is not using camelcase
        agreement.value.customerId = data[0].id
      }
      else {
        agreement.value.customerId = undefined
      }
    } catch (error) {
      console.error(error)
    }
  }
}

watch(() => customerForm.value.zipCode, async (value) => {
  customerForm.value.city = ''

  if (value && rules.value.zipCode(value) === true) {
    const { data } = await axios.get(`https://api.dataforsyningen.dk/postnumre?nr=${value}`)

    customerForm.value.city = (data as any[])[0]?.navn ?? ''
  }
})

onBeforeRouteLeave(() => {
  customerStore.savedCustomer = customerForm.value as CustomerDto
  local.setItem('customerForm', JSON.parse(JSON.stringify(customerForm.value)))
})

onMounted(async () => {
  if (iframeStore.isIframeMode && iframeStore.agreementResponsible) {
    agreement.value.agreementresponsible = iframeStore.agreementResponsible
  }

  const savedCustomerForm = await local.getItem('customerForm')

  if (savedCustomerForm) {
    customerForm.value = savedCustomerForm
  }
})
</script>

<template>
  <v-container>
    <v-form
      v-model="valid"
      :disabled="locked"
    >
    <v-text-field
        v-model="customerForm.email"
        label="Email"
        name="email"
        type="email"
        :rules="[rules.required, rules.email]"
        clearable
        @update:focused="getCustomer"
      />
      <v-text-field
        v-model="customerForm.name"
        label="Navn"
        name="name"
        type="text"
        :rules="[rules.required, rules.name]"
        clearable
      />
      <v-text-field
        v-model="customerForm.mobile"
        label="Telefon"
        prefix="+45"
        name="mobile"
        type="tel"
        :rules="[rules.required, rules.mobile]"
        clearable
      />
      <v-text-field
        v-model="customerForm.address1"
        label="Adresse"
        name="address"
        type="text"
        :rules="[rules.required, rules.address]"
        clearable
      />
      <v-text-field
        v-model="customerForm.zipCode"
        label="Postnummer"
        name="zipCode"
        type="tel"
        :rules="[rules.required, rules.zipCode]"
        clearable
      />
      <v-text-field
        v-model="customerForm.city"
        label="By"
        name="city"
        type="text"
        :rules="[rules.required]"
        placeholder="Udfyldes automatisk baseret på postnummer"
      />
      <v-select
        v-if="config?.options?.length && !iframeStore.isIframeMode"
        v-model="agreement.agreementresponsible"
        :items="config?.options"
        :label="config?.optionsLabel ?? 'Vælg venligst'"
        :disabled="false"
      />
    </v-form>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/addons" />
    <NextComponent
      to="/agreement/confirm"
      :disabled="!valid || (!iframeStore.isIframeMode && !!config?.options?.length && !agreement.agreementresponsible)"
    />
  </BottomBarComponent>
</template>